import { Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./routes/LoginPage";
import { NoAccessPage } from "./routes/NoAccessPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { ForgotPasswordPage } from "./routes/ForgotPasswordPage";
import { RequireAdmin, RequireCoachOrAdmin, RequireCoachOrAdminNotAssistant, RequireSignedIn } from "./auth/guards";
import { lazyWithReload as lazy } from "./lib/lazyWithReload";

// Eagerly load: LoginPage (auth gate, must render first), NoAccessPage,
// NotFoundPage, and AppLayout/guards (always present in the tree).
// Everything else is route-level code-split so the initial bundle stays small.
const HomePage = lazy(() =>
  import("./routes/HomePage").then((m) => ({ default: m.HomePage }))
);
const TournamentsPage = lazy(() =>
  import("./routes/TournamentsPage").then((m) => ({ default: m.TournamentsPage }))
);
const TournamentDetailPage = lazy(() =>
  import("./routes/TournamentDetailPage").then((m) => ({ default: m.TournamentDetailPage }))
);
const TatamiOperatorPage = lazy(() =>
  import("./routes/TatamiOperatorPage").then((m) => ({ default: m.TatamiOperatorPage }))
);
const TatamiDisplayPage = lazy(() =>
  import("./routes/TatamiDisplayPage").then((m) => ({ default: m.TatamiDisplayPage }))
);
const BracketsPrintPage = lazy(() =>
  import("./routes/BracketsPrintPage").then((m) => ({ default: m.BracketsPrintPage }))
);
const AthletesPage = lazy(() =>
  import("./routes/AthletesPage").then((m) => ({ default: m.AthletesPage }))
);
const DojosPage = lazy(() =>
  import("./routes/DojosPage").then((m) => ({ default: m.DojosPage }))
);
const CoachesPage = lazy(() =>
  import("./routes/CoachesPage").then((m) => ({ default: m.CoachesPage }))
);
const OperatorsPage = lazy(() =>
  import("./routes/OperatorsPage").then((m) => ({ default: m.OperatorsPage }))
);
const AssistantsPage = lazy(() =>
  import("./routes/AssistantsPage").then((m) => ({ default: m.AssistantsPage }))
);
const PaymentsPage = lazy(() =>
  import("./routes/PaymentsPage").then((m) => ({ default: m.PaymentsPage }))
);
const AttendancePage = lazy(() =>
  import("./routes/attendance/AttendancePage").then((m) => ({ default: m.AttendancePage }))
);
const ReportsLayout = lazy(() =>
  import("./routes/reports/ReportsLayout").then((m) => ({ default: m.ReportsLayout }))
);
const ReportsIndex = lazy(() =>
  import("./routes/reports/ReportsIndex").then((m) => ({ default: m.ReportsIndex }))
);
const TournamentDemographicsReport = lazy(() =>
  import("./routes/reports/TournamentDemographicsReport").then((m) => ({
    default: m.TournamentDemographicsReport
  }))
);
const CoachEfficiencyReport = lazy(() =>
  import("./routes/reports/CoachEfficiencyReport").then((m) => ({
    default: m.CoachEfficiencyReport
  }))
);
const DojoEfficiencyReport = lazy(() =>
  import("./routes/reports/DojoEfficiencyReport").then((m) => ({
    default: m.DojoEfficiencyReport
  }))
);
const TournamentFeesReport = lazy(() =>
  import("./routes/reports/TournamentFeesReport").then((m) => ({
    default: m.TournamentFeesReport
  }))
);
const DojoPaymentsReport = lazy(() =>
  import("./routes/reports/DojoPaymentsReport").then((m) => ({
    default: m.DojoPaymentsReport
  }))
);
const DisciplineMixReport = lazy(() =>
  import("./routes/reports/DisciplineMixReport").then((m) => ({
    default: m.DisciplineMixReport
  }))
);
const GrowthReport = lazy(() =>
  import("./routes/reports/GrowthReport").then((m) => ({
    default: m.GrowthReport
  }))
);
const InscriptionsByDojoReport = lazy(() =>
  import("./routes/reports/InscriptionsByDojoReport").then((m) => ({
    default: m.InscriptionsByDojoReport
  }))
);
const TournamentFinanceReport = lazy(() =>
  import("./routes/reports/TournamentFinanceReport").then((m) => ({
    default: m.TournamentFinanceReport
  }))
);
const MedalTableReport = lazy(() =>
  import("./routes/reports/MedalTableReport").then((m) => ({
    default: m.MedalTableReport
  }))
);
const InventoryLayout = lazy(() =>
  import("./routes/inventory/InventoryLayout").then((m) => ({ default: m.InventoryLayout }))
);
const InventoryItemsTab = lazy(() =>
  import("./routes/inventory/InventoryItemsTab").then((m) => ({
    default: m.InventoryItemsTab
  }))
);
const InventorySalesTab = lazy(() =>
  import("./routes/inventory/InventorySalesTab").then((m) => ({
    default: m.InventorySalesTab
  }))
);
const InventoryReportsTab = lazy(() =>
  import("./routes/inventory/InventoryReportsTab").then((m) => ({
    default: m.InventoryReportsTab
  }))
);

function RouteFallback() {
  return (
    <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
      …
    </div>
  );
}

function lazyRoute(node: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  // Operator-accessible routes (also admin/coach). Operators can only
  // reach the tournaments list + per-tournament detail; the rest of
  // the app (athletes/dojos/payments/coaches/reports/etc.) sits under
  // tighter guards below.
  {
    element: <RequireSignedIn />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "/", element: <Navigate to="/tournaments" replace /> },
          { path: "/tournaments", element: lazyRoute(<TournamentsPage />) },
          { path: "/tournaments/:tournamentId", element: lazyRoute(<TournamentDetailPage />) },
          // Tatami operator dashboard: live scoreboard + queue. OPERATOR's
          // primary work surface, so it lives under the signed-in guard
          // (not RequireAdmin). Backend gates scoreboard mutations with
          // requireOperatorOrAdmin already.
          {
            path: "/tournaments/:tournamentId/tatamis/:tatamiId/operator",
            element: lazyRoute(<TatamiOperatorPage />)
          }
        ]
      },
      // Fullscreen read-only display board for projecting at the tatami.
      // Deliberately a sibling of the AppLayout branch (still gated by
      // RequireSignedIn) so it renders WITHOUT nav/footer chrome and can
      // fill 100vw × 100vh.
      {
        path: "/tournaments/:tournamentId/tatamis/:tatamiId/display",
        element: lazyRoute(<TatamiDisplayPage />)
      },
      // Dedicated, chrome-less print view for category draws/brackets.
      // A sibling of the AppLayout branch (still gated by RequireSignedIn)
      // so it renders WITHOUT nav/footer/tab chrome — one category per page.
      {
        path: "/tournaments/:tournamentId/brackets/print",
        element: lazyRoute(<BracketsPrintPage />)
      }
    ]
  },
  {
    element: <RequireCoachOrAdmin />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "/home", element: lazyRoute(<HomePage />) },
          { path: "/athletes", element: lazyRoute(<AthletesPage />) },
          { path: "/dojos", element: lazyRoute(<DojosPage />) },
          { path: "/payments", element: lazyRoute(<PaymentsPage />) },
          { path: "/attendance", element: lazyRoute(<AttendancePage />) },
          {
            // Assistants management is coach/admin only — assistants can't
            // manage assistants (KAN-180); nav hides it, this enforces the URL.
            element: <RequireCoachOrAdminNotAssistant />,
            children: [
              { path: "/assistants", element: lazyRoute(<AssistantsPage />) }
            ]
          },
          // /reports is shared: admins see every sub-report, coaches
          // see only demographics + medallero (scoped to their dojos).
          // The admin-only sub-routes redirect non-admins to the
          // reports index from inside each component, so the URL bar
          // can't get around the filter.
          {
            path: "/reports",
            element: lazyRoute(<ReportsLayout />),
            children: [
              { index: true, element: lazyRoute(<ReportsIndex />) },
              // Coach-accessible (filtered to their own dojos server-side).
              {
                path: "tournament-demographics",
                element: lazyRoute(<TournamentDemographicsReport />)
              },
              {
                path: "medal-table",
                element: lazyRoute(<MedalTableReport />)
              },
              // Admin-only sub-routes nested under RequireAdmin so a
              // coach deep-linking the URL bounces to /no-access.
              {
                element: <RequireAdmin />,
                children: [
                  {
                    path: "coach-efficiency",
                    element: lazyRoute(<CoachEfficiencyReport />)
                  },
                  {
                    path: "dojo-efficiency",
                    element: lazyRoute(<DojoEfficiencyReport />)
                  },
                  {
                    path: "tournament-fees",
                    element: lazyRoute(<TournamentFeesReport />)
                  },
                  {
                    path: "tournament-finance",
                    element: lazyRoute(<TournamentFinanceReport />)
                  },
                  {
                    path: "dojo-payments",
                    element: lazyRoute(<DojoPaymentsReport />)
                  },
                  {
                    path: "discipline-mix",
                    element: lazyRoute(<DisciplineMixReport />)
                  },
                  {
                    path: "growth",
                    element: lazyRoute(<GrowthReport />)
                  },
                  {
                    path: "inscriptions-by-dojo",
                    element: lazyRoute(<InscriptionsByDojoReport />)
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: "/coaches", element: lazyRoute(<CoachesPage />) },
          { path: "/operators", element: lazyRoute(<OperatorsPage />) },
          {
            path: "/inventory",
            element: lazyRoute(<InventoryLayout />),
            children: [
              { index: true, element: <Navigate to="/inventory/items" replace /> },
              {
                path: "items",
                element: lazyRoute(<InventoryItemsTab />)
              },
              {
                path: "sales",
                element: lazyRoute(<InventorySalesTab />)
              },
              {
                path: "reports",
                element: lazyRoute(<InventoryReportsTab />)
              }
            ]
          }
        ]
      }
    ]
  },
  {
    element: <AppLayout />,
    children: [
      { path: "/no-access", element: <NoAccessPage /> },
      { path: "*", element: <NotFoundPage /> }
    ]
  }
]);
